import { call, put, take, fork, select, race } from 'redux-saga/effects';
import { debug, error } from '../utils/logger';
import {
    loginSuccess,
    signOutSuccess,
    sendPasswordResetSuccess,
    requestLoyaltySubscription,
    receiveLoyaltySubscription,
    requestUserAddresses,
    receiveUserAddresses,
    requestUserPreferences,
    receiveUserPreferences,
    REQUEST_LOYALTY_SUBSCRIPTION,
    RECEIVE_LOYALTY_SUBSCRIPTION,
    REQUEST_USER_ADDRESSES,
    REQUEST_USER_PREFERENCES,
    RECEIVE_USER_PREFERENCES,
    loyaltyViewReady,
    LOYALTY_VIEW_READY,
    disableLoyaltyModal,
    emailSignupFailure,
    stickySignUpSuccess,
    setLoyaltyStatus
} from '../actions/userActions.js';

import {
    requestCartItems,
    RECEIVE_PROFILE,
    userInitialized
} from '../actions/initialActions.js';

import {
    TokenClient
} from 'urbnTokenLib';
import {
    LoginUtility,
    ProfileClient
} from '../loginUtility/loginUtility';
import {
    REQUEST_PROFILE,
    userLoggedIn,
    requestProfile,
    receiveProfile,
    handleUtagEvent,
    setTracerValue
} from '../actions/initialActions.js';
import {
    COOKIE_NAME_CURRENCY,
    COOKIE_NAME_LANGUAGE,
    COOKIE_NAME_LOGIN_SUCCESS,
    COOKIE_NAME_AUTH_PAYLOAD,
    COOKIE_NAME_SIGNUP_SUCCESS,
    COOKIE_NAME_URBN_IS_LOYALTY,
    COOKIE_NAME_URBN_LOYALTY_POINTS,
    COOKIE_PERSONALIZATION_CONTEXT,
    COOKIE_NAME_LOYALTY_BROWSE_EVENT,
    GENDER_TYPES,
    LOGIN_METHOD_SIGNIN,
    LOGIN_METHOD_SIGNUP,
    scopeTypes
} from '../constants';

import {
    USER_NAME_FINISHED,
    USER_NAME_CANCELED,
    userNameFinished,
    userNameError
} from '../actions/userNameActions.js';

import localCache from '../../../core/js/utils/cacheUtils';
import {
    makeRequest
} from './makeRequest';
import {
    getLoggedInState,
    getTokenTracerValue
} from '../../../core/js/utils/authUtils';
import {
    hasUsername,
    isRegistered,
} from '../utils/loginUtils';
import {
    launchModal,
    isModalOpen
} from '../utils/modalUtils';
import apiRoutes from '../utils/apiRoutes';
import { reloadPage } from '../utils';
import { isWebview } from '../utils/cookieUtils';
import {
    pushModalTransition,
    replaceModalTransition,
    popModalTransition
} from '../../../core/js/actions/modalActions';
import Cookies from '../services/cookies';

import { init as userNameModalInit } from '../components/userNameModalComponent';

import {
    init as emailSignupConfirmationInit
} from '../../../core/js/components/emailSignUpModalConfirmationComponent.js';
import {
    init as emailSignUpInit
} from '../../../core/js/components/emailSignUpModalComponent';

import {
    changeEmailOptinSource,
    setEmailSignupStickyState
} from '../actions/emailSignupActions';

import { updateButtonTextSaga, ANIMATED_DOTS } from '../../../core/js/sagas/animateButtonSagas';
import { privacyPreferencesCookieSaga, setPrivacyPreferencesSaga } from '@core/js/sagas/privacyPreferencesSagas';

import { triggerCountrySelect } from '../../../core/js/utils/eventUtils';

import { buildErrorsFromResponse } from '../utils/errorHandling.js';

import { fireDYEvent } from '../../../core/js/utils/dyUtils';

import { AB_STICKY } from '@core/js/abTestingConstants';

import { launchAsyncLoyaltyModal } from '@core/js/utils/asyncLaunchers';

export function* userLoginSaga(payload) {
    debug('Saga: userLoginSaga initialized');

    let state = yield select();
    const siteId = state.core.constants.siteId;
    const urls = state.core.constants.urls;
    const tokenClient = new TokenClient(siteId, urls.token);
    const profileClient = new ProfileClient(siteId, urls.profile);
    const loginUtility = new LoginUtility(tokenClient, profileClient);

    const loggedInTokenSession = yield call(attemptLogin, payload.formData, loginUtility, payload.method);
    const tracerValue = getTokenTracerValue();
    Raven.setUserContext({ id: tracerValue });
    try {
        yield call(createPreferencesIfNeeded, payload.method);
    } catch(err) {
        error('Error creating preferences', err);
    }

    try {
        const formData = payload.formData;

        if (formData && formData.emailSubscription) {
            debug("Creating email subscription");

            const source = formData.emailSubscription.source;
            const gender = formData.emailSubscription.gender;
            const utagData = {
                event_name: 'email_signup',
                customer_email: formData.emailAddress
            };
            yield fork(createSubscriptionsIfNeeded, payload.method, source, gender);
            yield put(handleUtagEvent(utagData, 'link'));
        }
    } catch (err) {
        error('Error creating subscriptions', err);
    }

    yield put(loginSuccess(loggedInTokenSession));

    Cookies.deleteCookie(COOKIE_PERSONALIZATION_CONTEXT);

    // request profile on login success for tealium tagging
    yield put(requestProfile(payload.method));
    yield take(RECEIVE_PROFILE);

    // Update Cart
    yield put(requestCartItems());
    state = yield select();

    const loyaltyEnabled = _.get(state, 'core.constants.loyaltyOptions.loyalty_enabled');

    if (loyaltyEnabled) {
        if (!excludedLoyaltyRoute(state) && !excludeLoyaltyFromLogin(state)) {
            yield put(disableLoyaltyModal(false));
        } else {
            yield put(disableLoyaltyModal(true));
        }

        // Need to request loyalty on login to set loyalty cookie and show modal if it's enabled
        yield put(requestLoyaltySubscription());
        yield take(RECEIVE_LOYALTY_SUBSCRIPTION);
    } else {
        yield put(disableLoyaltyModal(true));
    }

    return loggedInTokenSession;
}

export function* userSignOutSaga() {
    debug('Saga: userSignOutSaga initialized');
    const state = yield select();
    const siteId = state.core.constants.siteId;
    const urls = state.core.constants.urls;
    const tokenClient = new TokenClient(siteId, urls.token);
    const profileClient = new ProfileClient(siteId, urls.profile);
    const loginUtility = new LoginUtility(tokenClient, profileClient);
    const logoutUtagData = {
        event_name: 'user_logout'
    };

    yield call([loginUtility, loginUtility.signOut]);
    yield put(signOutSuccess(''));
    yield put(handleUtagEvent(logoutUtagData, 'link'));
    Cookies.deleteCookie(COOKIE_NAME_URBN_IS_LOYALTY);
    Cookies.deleteCookie(COOKIE_NAME_URBN_LOYALTY_POINTS);
    Cookies.deleteCookie(COOKIE_PERSONALIZATION_CONTEXT);
    Cookies.deleteCookie(COOKIE_NAME_LOYALTY_BROWSE_EVENT);
    localCache.clear();
    yield put(setLoyaltyStatus(false));
    yield call(reloadPage);
}

function* createPreferencesIfNeeded(method) {
    const state = yield select();
    debug('createPreferencesIfNeeded', method);

    if (method === LOGIN_METHOD_SIGNUP) {
        const requestBody = {
            global: {
                language: state.core.language || Cookies.getCookieByName(COOKIE_NAME_LANGUAGE),
                country: state.core.userLocation.country_code || 'US',
                currency: state.core.currency || Cookies.getCookieByName(COOKIE_NAME_CURRENCY)
            }
        };

        const route = {
            method: 'POST',
            path: `${state.core.constants.urls.profile}/${state.core.constants.siteId}/me/preferences`,
            entity: requestBody
        };

        return yield call(makeRequest, route);
    }
}

export function* createSubscriptionsIfNeeded(method, source, gender) {
    const state = yield select();
    debug('createSubscriptionsIfNeeded', method);

    if (method === LOGIN_METHOD_SIGNUP) {
        let route = apiRoutes.createEmailSubscription(state, source, gender);
        route.failSilently = true;

        return yield call(makeRequest, route);
    }
}

function attemptLogin(formData, loginUtil, method) {
    if (method === LOGIN_METHOD_SIGNIN) {
        return loginUtil.signIn(formData.emailAddress, formData.password);
    } else if (method === LOGIN_METHOD_SIGNUP) {
        const gender = _.get(formData, 'emailSubscription.gender', GENDER_TYPES.UNKNOWN);
        const firstName = _.get(formData, 'name.firstName', '');

        const genderPayload = {
            'gender': gender
        };

        const profileNamePayload = {
            'name': {
                'first': firstName
            }
        };

        const otherProfileData = Object.assign(
            {}, profileNamePayload, genderPayload
        );

        return loginUtil.signUp(formData.emailAddress, formData.password, formData.confirmPassword, otherProfileData);
    }
}

export function* submitForgotPasswordSaga(payload) {
    debug('Saga: submitForgotPasswordSaga initialized');

    const state = yield select();
    const siteId = state.core.constants.siteId;
    const urls = state.core.constants.urls;
    const tokenClient = new TokenClient(siteId, urls.token);
    const profileClient = new ProfileClient(siteId, urls.profile);
    const loginUtility = new LoginUtility(tokenClient, profileClient);

    yield call([loginUtility, loginUtility.forgotPassword], payload.email);
    yield put(sendPasswordResetSuccess());
}

export function* requestProfileSaga() {
    let payload;

    while ({ payload } = yield take(REQUEST_PROFILE)) {
        let state = yield select();

        const includeLoyalty = (payload.method === 'INCLUDE_LOYALTY');
        const includePrivacy = !_.isEmpty(_.get(state, 'core.privacyRestriction', false));
        const route = apiRoutes.getProfile(state, true, includeLoyalty, includePrivacy);

        const profile = yield call(makeRequest, route);
        yield put(receiveProfile(profile));

        // Since we want the isEmployee flag in our state, we can grab an updated
        // 👇 version of the state.
        state = yield select();

        const isLoggedIn = _.get(state, 'core.loggedIn', '');
        let userAuthPayload = Cookies.getCookieByName(COOKIE_NAME_AUTH_PAYLOAD);
        userAuthPayload = JSON.parse(decodeURIComponent(userAuthPayload));
        const userScope = _.get(userAuthPayload, 'scope', 'GUEST');
        const profileUtagData = {
            customer_auth_status: `${ userScope }`,
            customer_id: _.get(state, 'core.profile.profileId', ''),
            customer_session: _.get(state, 'core.profile.tracerValue', ''),
            customer_is_employee: _.get(state, 'core.profile.isEmployee', false),
            customer_email: _.get(state, 'core.profile.emailAddress', ''),
            customer_sterling_id: _.get(state, 'core.profile.sterlingId', '')
        };

        const emailAddress = profileUtagData.customer_email;
        if (isLoggedIn) {
            if (Cookies.getCookieByName(COOKIE_NAME_SIGNUP_SUCCESS)) {
                fireDYEvent('signup', emailAddress);
                Cookies.deleteCookie(COOKIE_NAME_SIGNUP_SUCCESS);
            }
            if (Cookies.getCookieByName(COOKIE_NAME_LOGIN_SUCCESS)) {
                fireDYEvent('login', emailAddress);
                Cookies.deleteCookie(COOKIE_NAME_LOGIN_SUCCESS);
            }
        }

        // if we truly have a profile, send custom event to let registry know
        // it's safe to resolve any promises waiting on the profile information
        if (_.get(state, 'core.profile.profileId')) {
            debug('Triggering profile:received');
            $.event.trigger({
                type: 'profile:received',
                payload: _.get(state, 'core.profile')
            });

            // if personalized account toggle is on, request global
            // user preferences for avatar image
            if (state.core.showPersonalizedAccountToggle) {
                yield put(requestUserPreferences('global'));
            }
        }

        // if requesting profile from a login method, fire utag link event
        // every other request should fire utag view event
        if (payload.method) {
            if (payload.method === LOGIN_METHOD_SIGNIN) {
                profileUtagData.event_name = 'user_login';
            } else if (payload.method === LOGIN_METHOD_SIGNUP) {
                profileUtagData.event_name = 'user_register';
            }
            yield put(handleUtagEvent(profileUtagData, 'link'));
        } else {
            yield put(handleUtagEvent(profileUtagData, 'view'));
        }
    }
}

export function* requestLoyaltySubscriptionSaga() {
    let payload;

    while ({ payload } = yield take(REQUEST_LOYALTY_SUBSCRIPTION)) {
        const state = yield select();
        const disableLoyaltyModalSetting = _.get(state, 'core.loyaltyForm.disableModal', false);

        try {
            // get loyalty profile
            const profileRoute = apiRoutes.getLoyaltySubscription(state, true);
            const profileRes = yield call(makeRequest, profileRoute);
            setUrbnLoyalty(true);
            yield put(receiveLoyaltySubscription(profileRes));

            if (!disableLoyaltyModalSetting) {
                yield put(loyaltyViewReady());
            }
        } catch(err) {
            // loyalty endpoint will throw a 404 if user is not a loyalty member
            setUrbnLoyalty(false);
            error('requestLoyaltySubscriptionSaga:', err);
            yield put(receiveLoyaltySubscription());

            // Request user addresses if a LOGGED IN user is not a loyalty member to display appropriate form
            if (!disableLoyaltyModalSetting && getLoggedInState()) {
                yield put(requestUserAddresses());
            } else {
                yield put(loyaltyViewReady());
            }
        }

        const loyaltyState = Cookies.getCookieByName(COOKIE_NAME_URBN_IS_LOYALTY) === 'true';
        yield put(setLoyaltyStatus(loyaltyState));
    }
}

function setUrbnLoyalty(value) {
    return Cookies.setCookie(COOKIE_NAME_URBN_IS_LOYALTY, value);
}

function setUrbnLoyaltyPoints(value) {
    return Cookies.setCookie(COOKIE_NAME_URBN_LOYALTY_POINTS, value);
}

export function* requestUserAddressesSaga() {
    let payload;

    while ({ payload } = yield take(REQUEST_USER_ADDRESSES)) {
        const state = yield select();

        try {
            const route = apiRoutes.getProfileAddresses(state, true);
            yield call(makeRequest, route);
            yield put(receiveUserAddresses());
            yield put(loyaltyViewReady());
        } catch(err) {
            error('requestUserAddressesSaga:', err);
            yield put(loyaltyViewReady());
        }
    }
}

export function* getCustomerInfoSaga() {
    debug("Saga: getCustomerInfoSaga initialized");

    const loggedInState = getLoggedInState();
    const tracerValue = getTokenTracerValue();
    Raven.setUserContext({ id: tracerValue });
    const customerSessionUtagData = {
        // eslint-disable-next-line camelcase
        customer_session: tracerValue
    };

    yield fork(privacyPreferencesCookieSaga);

    yield put(setTracerValue(tracerValue));

    yield put(userLoggedIn(loggedInState));

    if (loggedInState === true) {
        let state = yield select();
        const requestMethod = getProfileRequestMethod(state);
        yield put(requestProfile(requestMethod));
        // halt until a profile response is received
        yield take(RECEIVE_PROFILE);
        state = yield select();
        yield fork(handleLoyaltyEventsSaga, state);
    } else {
        yield put(handleUtagEvent(customerSessionUtagData, 'view'));
    }

    yield put(userInitialized());

}

export function* handlePrivacyPreferencesSaga() {
    let action;

    while (action = yield take(RECEIVE_PROFILE)) {
        yield fork(setPrivacyPreferencesSaga);
    }
}

/**
 * make request to user preferences endpoint
 */
export function* requestUserPreferencesSaga() {
    let payload;

    while ({ payload } = yield take(REQUEST_USER_PREFERENCES)) {
        const state = yield select();

        try {
            const route = payload.preferenceType === 'global'
                ? apiRoutes.getProfilePreferences(state)
                : apiRoutes.getProfileWebPreferences(state);
            const res = yield call(makeRequest, route);

            yield put(receiveUserPreferences(res));
        } catch(err) {
            error('requestUserPreferencesSaga:', err);
            yield put(receiveUserPreferences());
        }
    }
}

/**
 * If the user has not interstitial declined and is not a loyalty member - fire loyalty modal
 * @param {Event} e Global window event
 * @param {String} componentName Name of Component
 */
export function* loyaltyModalSaga(e, componentName) {
    debug('Saga: loyaltyModalSaga initialized');
    let state = yield select();

    yield put(requestUserPreferences('web'));
    yield take(RECEIVE_USER_PREFERENCES);

    state = yield select();
    const interstitialDeclined = _.get(state, 'core.profile.webPreferences.interstitialDeclined', false);
    const loyaltyMember = _.get(state, 'core.profile.loyalty.loyaltyId', false);

    if (!interstitialDeclined && !loyaltyMember && !isWebview()) {
        launchAsyncLoyaltyModal(e, componentName, 'replace', true);

        const loyaltyModalUtagData = {
            page_type: state.core.localizations.containers.analyticsContainer.account_loyalty_modal_page_type,
            page_id: state.core.localizations.containers.analyticsContainer.account_loyalty_modal_page_id,
            page_name: state.core.localizations.containers.analyticsContainer.account_loyalty_modal_page_name
        };

        yield put(handleUtagEvent(loyaltyModalUtagData, 'view'));
    } else {
        const payload = popModalTransition({}, componentName);
        $.event.trigger({
            type: payload.type,
            payload: payload
        });
    }
}

/**
 * Functions to be run when loyalty view is ready
 */
export function* loyaltyViewReadySaga() {
    debug('Saga: loyaltyViewReadySaga initialized');

    yield take(LOYALTY_VIEW_READY);

    triggerCountrySelect();
}

export function excludedLoyaltyRoute(state) {
    let excludedRoute = false;

    _.forEach(state.core.constants.loyaltyOptions.loyalty_modal_excluded_routes, (route) => {
        if (window.location.pathname.includes(route)) {
            excludedRoute = true;
        }
    });

    return excludedRoute;
}

export function excludeLoyaltyFromLogin(state) {
    const allowLoyalty = _.get(state,
        'core.featureConfigs.user-authorization-modal-feature.allowLoyaltyCaptureAfterSignInSignUp', true
    );

    if (!allowLoyalty) {
        return true;
    }

    return !state.core.modal || state.core.modal.originComponent !== 'headerComponent';
}

export function* handleEmailSignupSuccess(e, state) {
    if (isModalOpen()) {
        const payload = replaceModalTransition(emailSignupConfirmationInit, {}, e, 'modal.emailSignupConfirmation');

        $.event.trigger({
            type: payload.type,
            payload: payload
        });
    } else {
        const payload = pushModalTransition(emailSignupConfirmationInit, {}, e, 'modal.emailSignupConfirmation');

        $.event.trigger({
            type: payload.type,
            payload: payload
        });
    }
}

export function* emailSignupSaga(formData, e, component) {
    const state = yield select();
    try {
        // NOTE As far as my search went, none of the brands use gender on email sign up. Leaving as unknown.
        const route = apiRoutes.createAnonymousEmailSubscription(state, formData.source, formData.emailAddress, GENDER_TYPES.UNKNOWN, formData.extraRequestData);
        const utagData = {
            event_name: 'email_signup',
            customer_email: formData.emailAddress
        };

        if (state.core.loggedIn) {
            const emailAddress = _.get(state, 'core.profile.emailAddress');
            fireDYEvent('newsletter', emailAddress);
        }

        yield call(makeRequest, route);
        yield call(handleEmailSignupSuccess, e, state);
        yield put(handleUtagEvent(utagData, 'link'));

        if(component.storeRoot === 'footer') {
            yield put(stickySignUpSuccess());
        }
    } catch (err) {
        error('Error submitting email for sign up', err);
        // only show service errors if the signup originated from the footer
        if (_.get(component, 'originatedFromOutsideAccount', false)) {
            yield put(emailSignupFailure(err, component.storeRoot));
        } else { // otherwise act as if we succeeded to allow the user to start shopping
            yield call(handleEmailSignupSuccess, e, state);
            if(component.storeRoot === 'footer') {
                yield put(stickySignUpSuccess());
            }
        }
    }
}

export function* emailSignupPersonalizeSaga(formData, extraRequestData) {
    const state = yield select();
    const route = apiRoutes.createAnonymousEmailSubscription(
        state, formData.source, formData.emailAddress, GENDER_TYPES.UNKNOWN, extraRequestData
    );

    const makeSignupRequest = call(makeRequest, route);
    const updateOptions = {
        pendingText: ANIMATED_DOTS,
        changeToText: 'Submitted',
        changeBackText: 'Submitted'
    };
    yield call(updateButtonTextSaga, makeSignupRequest, '.js-email-personalize-signup', 2000, updateOptions);
}

export function* emailSignupMarketingSaga() {
    debug('Saga: handleEmailSignupMarketingSaga initialized');

    const state = yield select();
    const abSticky = Cookies.getCookieByName(AB_STICKY);

    // check SS cookie and handle accordingly
    if (abSticky === '1') {
        const stickySource = _.get(
            state.core,
            'featureConfigs.stickyFooterEmailSubscriptions.source',
            "Sticky Sign Up"
        );
        yield put(changeEmailOptinSource('footer', stickySource));
        yield put(setEmailSignupStickyState(true));
    } else if (abSticky === '2') {
        return;
    } else {

        const emailSignUpOverrides = {
            originatedFromOutsideAccount: true,
            optInSource: _.get(
                state,
                'core.featureConfigs.lightboxAnonymousEmailSubscriptions.source',
                'Lightbox'
            )
        };

        const payload = yield put(pushModalTransition(emailSignUpInit, emailSignUpOverrides));

        $.event.trigger({
            type: payload.type,
            payload: payload
        });
    }
}

export function* userNameModalSaga(payload, componentName) {
    const state = yield select();

    try {
        const entity = {
            'emailAddress': state.core.profile.emailAddress,
            'loyaltyUserName': payload.userName
        };
        const route = apiRoutes.updateProfile(state, entity);
        yield call(makeRequest, route);

        yield put(requestProfile());
        yield take(RECEIVE_PROFILE);

        yield put(userNameFinished());

        const closePayload = popModalTransition({}, componentName);
        $.event.trigger({
            type: closePayload.type,
            payload: closePayload
        });
    } catch(err) {
        error('userNameModalSaga:', err);
        const msgs = buildErrorsFromResponse(err, 'USERNAME_MODAL');
        yield put(userNameError(msgs));
    }
}

export function* handleUserNameRequiredSaga() {
    debug("handleUserNameRequiredSaga: start");

    const state = yield select();

    const userNameState = {
        canceled: false,
        finished: false
    };

    if (!hasUsername(state)) {
        debug("User does not have a loyalty username");
        launchModal(userNameModalInit);

        const { canceled, finished } = yield race({
            canceled: take(USER_NAME_CANCELED),
            finished: take(USER_NAME_FINISHED)
        });

        userNameState.canceled = canceled;
        userNameState.finished = finished;

    } else {
        debug("User has a loyalty username");
        userNameState.finished = true;
    }

    return userNameState;
}

export function* handleLoyaltyEventsSaga(state) {
    const loyaltyVendor = _.get(state, 'core.constants.loyaltyOptions.loyalty_vendor');
    const isLoyaltyVendorSessionM = loyaltyVendor === 'session_m';
    const loyaltyBrowseEventCookie = Cookies.getCookieByName(COOKIE_NAME_LOYALTY_BROWSE_EVENT);
    const loyaltyId = _.get(state, 'core.profile.loyalty.loyaltyId');
    const loyaltyCookie = Cookies.getCookieByName(COOKIE_NAME_URBN_IS_LOYALTY) === 'true';
    const isLoyaltyMember = loyaltyCookie || loyaltyId;

    if (isLoyaltyVendorSessionM && !loyaltyBrowseEventCookie && isLoyaltyMember) {
        // set loyalty browse event cookie for 24hrs
        Cookies.setCookie(COOKIE_NAME_LOYALTY_BROWSE_EVENT, 'true', 86400);
        const loyaltyEventData = {
            'events': {
                'crosschannel_browse_event': {}
            }
        };
        const route = apiRoutes.sendLoyaltyEvents(state, loyaltyEventData);
        route.failSilently = true;
        yield call(makeRequest, route);
    }
}

function getProfileRequestMethod(state) {
    // conditionally include loyalty on page load profile request for loyalty browse event logic
    const loyaltyVendor = _.get(state, 'core.constants.loyaltyOptions.loyalty_vendor');
    const isLoyaltyVendorSessionM = loyaltyVendor === 'session_m';
    const loyaltyBrowseEventCookie = Cookies.getCookieByName(COOKIE_NAME_LOYALTY_BROWSE_EVENT);
    const loyaltyRequestRequired = isLoyaltyVendorSessionM && isRegistered && !loyaltyBrowseEventCookie;

    return loyaltyRequestRequired ? 'INCLUDE_LOYALTY' : '';
}

export default [
    requestProfileSaga,
    requestLoyaltySubscriptionSaga,
    requestUserAddressesSaga,
    requestUserPreferencesSaga,
    loyaltyViewReadySaga,
    handlePrivacyPreferencesSaga
];
